import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Typography, Avatar, Button, Grid, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Alert, InputAdornment, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getCustomerById } from "../../redux/slices/myAccount/customerDataSlice";
import Loading from "../UI/Loading/Loading";
import { useLanguage } from "../../context/LanguageContext/LanguageContext";
import { changePassword } from "../../redux/slices/myAccount/changePasswordSlice";
import MessageSnackbar from "../SuccessSnackbar/SuccessSnackbar";
import NotFound from "../NotFoundService/NotFoundService";
import { Visibility, VisibilityOff } from "@mui/icons-material";
// Reusable ProfileCard Component
const ProfileCard = ({ name, email, phone, nationalId, onResetPassword, direction, t }) => {
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  };

  return (
    <Card sx={{ margin: "0 auto", borderRadius: 4, boxShadow: 3 }}>
      <CardContent sx={{ display: "flex", alignItems: "center", padding: 3 }}>
        <Avatar sx={{ bgcolor: "#12538B", color: "#fff", width: 70, height: 70, fontSize: 28 }}>
          {getInitials(name)}
        </Avatar>
        <Box sx={{ marginLeft: 2, mr: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{name}</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: "6px" }}>{email}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// Personal Information Section Card
const PersonalInformationCard = ({ name, email, phone, nationalId, direction, t }) => (
  <Card sx={{ marginTop: 2, borderRadius: 4, boxShadow: 3 }}>
    <CardContent sx={{ padding: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom mb={2}>
        {t.personalInformation}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography variant="body1">{t.name}:</Typography>
            <Typography variant="body1">{name}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography variant="body1">{t.email}:</Typography>
            <Typography variant="body1">{email}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography variant="body1">{t.phoneNumber}:</Typography>
            <Typography variant="body1">{phone}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography variant="body1">{t.nationalID}:</Typography>
            <Typography variant="body1">{nationalId}</Typography>
          </Box>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

// Change Password Section Card
const ChangePasswordCard = ({ onResetPassword, direction, t }) => (
  <Card sx={{ marginTop: 2, borderRadius: 4, boxShadow: 3 }}>
    <CardContent sx={{ padding: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
        {t.changePassword}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", gap: 5 }}>
          <Typography sx={{ fontSize: "16px" }}>{t.password}</Typography>
          <Typography>***********</Typography>
        </Box>
        <Button variant="contained" sx={{ backgroundColor: "var(--primary-color)", color: "#fff", padding: "10px 20px" }} onClick={onResetPassword}>
          {t.resetPassword}
        </Button>
      </Box>
    </CardContent>
  </Card>
);

// Main Profile Component
const Profile = () => {
  const { isLoggedIn, user, token } = useSelector(state => state.login);
  const { success, loading: passwordLoading, error: passwordError } = useSelector(state => state.changePassword);
  const { translations, direction, language } = useLanguage();
  const t = translations;
  const { customer, loading, error } = useSelector(state => state.customerData);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [formError, setFormError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // Can be 'error' or 'success'
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);
  const handleClickShowOldPassword = () => setShowOldPassword(!showOldPassword);
  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleClickShowNewPasswordConfirm = () => setShowNewPasswordConfirm(!showNewPasswordConfirm);

  useEffect(() => {
    if (isLoggedIn && user?.id) {
      dispatch(getCustomerById({userId:user.id,token})); 
    } else {
      navigate('/signin');
    }
  }, [dispatch, user?.id, isLoggedIn, navigate]);
 
  if (loading) return <Loading />;
  const handleDialogClose = () => {
    setOpenDialog(false);
    // Clear form fields when dialog is closed
    setOldPassword("");
    setNewPassword("");
    setNewPasswordConfirm("");
    setFormError("");
  };
  const handleResetPassword = () => {
    // Validate the form inputs
    if (!oldPassword || !newPassword || !newPasswordConfirm) {
      setFormError(t.fillAllFields);
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      setFormError(t.passwordMismatch); // Show message if passwords don't match
      return;
    }

    // Reset password by dispatching action
    const id = user.id;
    const type = 1;

    dispatch(changePassword({ oldPassword, newPassword, newPasswordConfirm, id, type, token }))
      .then(response => {
        if (response.payload.successtate === 200) {
          // Handle success state 200
          setFormError("");  // Clear any previous error if successful
          setSnackbarMessage(t.passwordChangedSuccessfully); // Set success message
          setSnackbarSeverity('success'); // Set severity to success
          setSnackbarOpen(true); // Show the snackbar
                    setOpenDialog(false);  // Close dialog on success
        } else if (response.payload.successtate !== 304) {
          // Handle other success cases (not 304)
          setFormError("");  // Clear any previous error if successful
          setOpenDialog(false);  // Close dialog on success
        } else {
          setFormError(response.payload.errormessage || t.genericError);
        }
      })
      .catch(err => {
        setFormError(t.genericError);
      });
  };

  if (!customer || customer.length === 0) {
    return <NotFound message={t.customerDataNotAvailable} />;
}

  if (!isLoggedIn) {
    navigate('/signin');
    return null;
  }

  return (
    <Box sx={{ padding: 3, backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
      <MessageSnackbar
      open={snackbarOpen}
      message={snackbarMessage}
      onClose={() => setSnackbarOpen(false)} // Close snackbar when the user clicks on it or after the timeout
      severity={snackbarSeverity}
    />
      <ProfileCard
        name={customer.customerName || ''}
        email={customer.email || ''}
        phone={customer.phoneNumber || ''}
        nationalId={customer.taxId || ''}
        t={t} direction={direction}
      />
      <PersonalInformationCard
        name={customer.customerName || ''}
        email={customer.email || ''}
        phone={customer.phoneNumber || ''}
        nationalId={customer.taxId || ''}
        t={t} direction={direction}
      />
      <ChangePasswordCard onResetPassword={() => setOpenDialog(true)} t={t} direction={direction} />
      <Dialog
      open={openDialog}
      onClose={() => setOpenDialog(false)}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "var(--secondary-color)", // Set background color for dialog
          borderRadius: "10px",
          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
          
        },
      }}
    >
      <DialogTitle sx={{ color: "var(--primary-color)" }}>{t.resetPassword}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t.oldPassword}
          type={showOldPassword ? "text" : "password"}
          fullWidth
          variant="filled"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          sx={{
            mb: 2,
            "& .MuiInputBase-root": {
              backgroundColor: "var(--secondary-color)", // Custom background for input fields
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--primary-color)", // Custom border color for input fields
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowOldPassword}>
                  {showOldPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="dense"
          label={t.newPassword}
          type={showNewPassword ? "text" : "password"}
          fullWidth
          variant="filled"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          sx={{
            mb: 2,
            "& .MuiInputBase-root": {
              backgroundColor: "var(--secondary-color)", // Custom background for input fields
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--primary-color)", // Custom border color for input fields
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowNewPassword}>
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="dense"
          label={t.confirmNewPassword}
          type={showNewPasswordConfirm ? "text" : "password"}
          fullWidth
          variant="filled"
          value={newPasswordConfirm}
          onChange={(e) => setNewPasswordConfirm(e.target.value)}
          sx={{
            mb: 2,
            "& .MuiInputBase-root": {
              backgroundColor: "var(--secondary-color)", // Custom background for input fields
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--primary-color)", // Custom border color for input fields
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowNewPasswordConfirm}>
                  {showNewPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
       
        />
        {formError && <Alert severity="error">{formError}</Alert>}
      </DialogContent>
      <DialogActions sx={{justifyContent:direction==="ltr"? "flex-end":"flex-start"}}>
        <Button onClick={handleDialogClose} sx={{ color: "var(--primary-color)" }}>
          {t.cancel}
        </Button>
        <Button
          onClick={handleResetPassword}
          sx={{
            color: "#fff",
            backgroundColor: "var(--primary-color)",
            "&:hover": {
              backgroundColor: "var(--primary-color-dark)",
            },
          }}
        
        >
          {t.resetPassword}
        </Button>
      </DialogActions>
    </Dialog>
      </Box>
  );
};

export default Profile;
